import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon.js';
import { supabase } from '../supabase.js';
import './ScheduleSlideOver.css';

// Unit options (for "every X days/weeks/months")
const UNIT_OPTIONS = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' }
];

// Generate interval number options (1-30)
const INTERVAL_NUMBERS = Array.from({ length: 30 }, (_, i) => i + 1);

// Format time from 24h to 12h with AM/PM
const formatTimeDisplay = (timeValue) => {
  if (!timeValue) return '';
  const [hour = '', minute = ''] = timeValue.split(':');
  if (hour.length === 0 || minute.length === 0) return timeValue;
  const hourNum = parseInt(hour, 10);
  if (Number.isNaN(hourNum)) return timeValue;
  const suffix = hourNum >= 12 ? 'PM' : 'AM';
  const normalizedHour = ((hourNum + 11) % 12) + 1;
  return `${normalizedHour}:${minute} ${suffix}`;
};

// Floating Success Animation component
const FloatingSuccess = ({ show, message, onComplete }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show) {
      setVisible(true);

      // Animation sequence - match the XP popup timing
      const timer = setTimeout(() => {
        setVisible(false);
        setTimeout(onComplete, 200); // Allow fade out time
      }, 2000); // 2 seconds like the XP popup

      return () => clearTimeout(timer);
    }
  }, [show, onComplete]);

  if (!show) return null;

  return createPortal(
    <div className={`floating-success ${visible ? 'visible' : ''}`}>
      <Icon name="CheckCircle" className="icon--sm" />
      <span>{message}</span>
    </div>,
    document.body
  );
};

// Get biohacks from protocol (handles both formats)
const getBiohacksFromProtocol = (protocol) => {
  if (protocol?.biohacks && Array.isArray(protocol.biohacks)) {
    return protocol.biohacks;
  }
  if (protocol?.protocol_hacks && Array.isArray(protocol.protocol_hacks)) {
    return protocol.protocol_hacks
      .sort((a, b) => (a.position || 0) - (b.position || 0))
      .map(ph => ph.biohacks)
      .filter(Boolean);
  }
  if (protocol?.hacks && Array.isArray(protocol.hacks)) {
    return protocol.hacks;
  }
  return [];
};

const ScheduleSlideOver = ({
  isOpen,
  onClose,
  protocol,
  onSave,
  userId
}) => {
  const [expandedBiohacks, setExpandedBiohacks] = useState({});
  const [scheduleData, setScheduleData] = useState({});
  const [originalScheduleData, setOriginalScheduleData] = useState({});
  const [bulkTime, setBulkTime] = useState('');
  const [bulkInterval, setBulkInterval] = useState('');
  const [bulkIntervalUnit, setBulkIntervalUnit] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showClearWarning, setShowClearWarning] = useState(false);

  const biohacks = useMemo(() => getBiohacksFromProtocol(protocol), [protocol]);

  // Load schedule data when protocol changes
  useEffect(() => {
    // Clear success message when sidebar opens
    if (isOpen) {
      setSaveSuccess(false);
    }
    
    const loadScheduleData = async () => {
      if (!protocol || !isOpen || !userId) return;

      try {
        // Fetch existing active schedule data for this protocol
        const { data, error } = await supabase
          .from('logging_schedules')
          .select('hack_id, time, unit, number')
          .eq('user_id', userId)
          .eq('protocol_id', protocol.id)
          .eq('is_active', true);

        if (error) {
          console.error('Failed to load schedule data:', error);
        }

        // Build schedule data map
        const scheduleMap = {};

        // Initialize with empty schedules for all biohacks
        biohacks.forEach(biohack => {
          scheduleMap[biohack.id] = {
            times: [],
            interval: null, // number (e.g., 2 for "every 2 days")
            intervalUnit: null // 'days', 'weeks', 'months'
          };
        });

        // Override with actual data if available
        if (data && data.length > 0) {
          // Group by hack_id - handle multiple time entries per hack
          const hackSchedules = {};
          data.forEach(entry => {
            const hackId = entry.hack_id;
            if (!hackId) return;
            
            if (!hackSchedules[hackId]) {
              const unit = entry.unit || 'days';
              const number = entry.number || 1;
              
              hackSchedules[hackId] = {
                times: [],
                interval: number,
                intervalUnit: unit
              };
            }
            if (entry.time) {
              hackSchedules[hackId].times.push(entry.time);
            }
          });

          // Update schedule map with real data
          Object.keys(hackSchedules).forEach(hackId => {
            if (scheduleMap[hackId]) {
              scheduleMap[hackId] = hackSchedules[hackId];
            }
          });
        }

        setScheduleData(scheduleMap);
        setOriginalScheduleData(JSON.parse(JSON.stringify(scheduleMap)));
      } catch (error) {
        console.error('Error loading schedule data:', error);
        // Fall back to empty schedules
        const defaultData = {};
        biohacks.forEach(biohack => {
          defaultData[biohack.id] = {
            times: [],
            interval: null,
            intervalUnit: null
          };
        });
        setScheduleData(defaultData);
        setOriginalScheduleData(JSON.parse(JSON.stringify(defaultData)));
      }
    };

    loadScheduleData();
  }, [protocol, isOpen, userId, biohacks]);

  // Check for unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(scheduleData) !== JSON.stringify(originalScheduleData);
  }, [scheduleData, originalScheduleData]);

  // Check if there are any schedules set
  const hasAnySchedules = useMemo(() => {
    return Object.values(scheduleData).some(s => 
      (s.interval !== null && s.interval !== undefined && s.intervalUnit) || 
      (s.times && s.times.length > 0)
    );
  }, [scheduleData]);

  // Determine protocol schedule summary from schedule data
  const protocolScheduleSummary = useMemo(() => {
    const schedules = Object.values(scheduleData)
      .filter(s => s.interval !== null && s.interval !== undefined && s.intervalUnit);
    if (schedules.length === 0) return 'Not Scheduled';
    // If all have the same schedule, show it
    const firstSchedule = schedules[0];
    const allSame = schedules.every(s => 
      s.interval === firstSchedule.interval && s.intervalUnit === firstSchedule.intervalUnit
    );
    if (allSame) {
      const unitLabel = UNIT_OPTIONS.find(opt => opt.value === firstSchedule.intervalUnit)?.label || firstSchedule.intervalUnit;
      return `Every ${firstSchedule.interval} ${unitLabel}`;
    }
    return 'Custom';
  }, [scheduleData]);

  // Handle closing the slide-over
  const handleClose = () => {
    setExpandedBiohacks({});
    setBulkTime('');
    setBulkInterval('');
    setBulkIntervalUnit('');
    setSaveSuccess(false);
    // Reset to original data if there are unsaved changes
    if (hasUnsavedChanges) {
      setScheduleData(JSON.parse(JSON.stringify(originalScheduleData)));
    }
    onClose();
  };

  // Handle saving the schedule
  const handleSave = async () => {
    try {
      await onSave(protocol.id, scheduleData);
      // Update original data after successful save
      setOriginalScheduleData(JSON.parse(JSON.stringify(scheduleData)));
      // Show success indicator
      setSaveSuccess(true);
    } catch (error) {
      console.error('Failed to save schedule:', error);
      // Don't close on error
    }
  };

  // Toggle biohack expansion
  const toggleBiohackExpansion = (biohackId) => {
    setExpandedBiohacks(prev => ({
      ...prev,
      [biohackId]: !prev[biohackId]
    }));
  };

  // Handle "Done" button - collapse the row
  const handleRowDone = (biohackId) => {
    setExpandedBiohacks(prev => ({
      ...prev,
      [biohackId]: false
    }));
  };

  // Update biohack schedule
  const updateBiohackSchedule = (biohackId, field, value) => {
    // Clear success message when user makes changes
    if (saveSuccess) {
      setSaveSuccess(false);
    }
    setScheduleData(prev => ({
      ...prev,
      [biohackId]: {
        ...prev[biohackId] || { times: [], interval: null, intervalUnit: null },
        [field]: value
      }
    }));
  };

  // Apply bulk settings to all biohacks that don't have schedules yet
  const applyBulkSettings = () => {
    if (!bulkInterval || !bulkIntervalUnit || !bulkTime) return;

    const updatedData = { ...scheduleData };
    Object.keys(updatedData).forEach(biohackId => {
      const currentSchedule = updatedData[biohackId];
      // Only apply to biohacks without schedules or with empty schedules
      if (!currentSchedule.interval || !currentSchedule.intervalUnit) {
        if (bulkInterval && bulkIntervalUnit) {
          updatedData[biohackId].interval = parseInt(bulkInterval, 10);
          updatedData[biohackId].intervalUnit = bulkIntervalUnit;
        }
        if (bulkTime) {
          updatedData[biohackId].times = [bulkTime];
        }
      }
    });
    setScheduleData(updatedData);
    // Clear bulk inputs after applying
    setBulkTime('');
    setBulkInterval('');
    setBulkIntervalUnit('');
  };

  // Clear all schedules
  const handleClearSchedule = () => {
    setShowClearWarning(true);
  };

  // Confirm clear schedule
  const handleConfirmClear = () => {
    const clearedData = {};
    biohacks.forEach(biohack => {
      clearedData[biohack.id] = {
        times: [],
        interval: null,
        intervalUnit: null
      };
    });
    setScheduleData(clearedData);
    setShowClearWarning(false);
    // Clear success message when clearing
    if (saveSuccess) {
      setSaveSuccess(false);
    }
  };

  // Cancel clear schedule
  const handleCancelClear = () => {
    setShowClearWarning(false);
  };

  if (!isOpen || !protocol) return null;

  const protocolStatus = protocol.status || 'staged';

  return (
    <>
      {/* Floating Success Animation */}
      <FloatingSuccess
        show={saveSuccess}
        message="Schedule saved successfully"
        onComplete={() => setSaveSuccess(false)}
      />

      {/* Clear Schedule Warning Modal */}
      {showClearWarning && (
        <div className="modal-overlay" onClick={(e) => {
          // Don't close on overlay click - user must explicitly choose
          e.stopPropagation();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Clear Schedule</h3>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <div style={{ 
                marginBottom: '24px', 
                fontSize: '14px', 
                lineHeight: '1.6', 
                color: 'var(--color-text-primary)'
              }}>
                <p style={{ marginBottom: '16px' }}>
                  Are you sure you want to clear all schedules for <strong>{protocol?.name || 'this protocol'}</strong>?
                </p>
                <p style={{ marginBottom: '0' }}>
                  This will remove all scheduled logging times for all biohacks in this protocol. You can add new schedules at any time.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  className="btn btn--secondary btn--md btn--rounded"
                  onClick={handleCancelClear}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--md btn--rounded"
                  onClick={handleConfirmClear}
                  style={{ flex: 1 }}
                >
                  Clear Schedule
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop */}
      <div
        className="schedule-slide-over-backdrop"
        onClick={handleClose}
      />

      {/* Slide-over panel */}
      <div className="schedule-slide-over">
        {/* Mobile Handle (matches ProtocolBuilder) */}
        <div 
          className="schedule-mobile-handle"
          onClick={handleClose}
        >
          <div className="mobile-handle-content">
            <span className="mobile-handle-title">
              Logging Schedule
            </span>
            <Icon 
              name="ChevronDown" 
              className="icon--sm" 
            />
          </div>
        </div>

        {/* Header */}
        <div className="schedule-slide-over-header">
          <div className="schedule-slide-over-header-content">
            <div className="schedule-slide-over-title-section">
              <h2 className="schedule-slide-over-header-title">
                <span>Logging Schedule</span>
              </h2>
            </div>
            <button
              className="schedule-slide-over-close-btn"
              onClick={handleClose}
              aria-label="Close schedule panel"
            >
              <Icon name="X" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="schedule-slide-over-body">
          <div className="schedule-slide-over-content">
            {/* Biohack Rows */}
            {biohacks.length === 0 ? (
              <div className="schedule-no-biohacks">
                <p>No biohacks in this protocol</p>
              </div>
            ) : (
              biohacks.map(biohack => (
                <BiohackRow
                  key={biohack.id}
                  biohack={biohack}
                  isExpanded={expandedBiohacks[biohack.id] || false}
                  scheduleData={scheduleData[biohack.id] || { times: [], interval: null, intervalUnit: null }}
                  onToggleExpansion={() => toggleBiohackExpansion(biohack.id)}
                  onUpdateSchedule={(field, value) => updateBiohackSchedule(biohack.id, field, value)}
                  onDone={() => handleRowDone(biohack.id)}
                />
              ))
            )}

          </div>
        </div>

        {/* Footer */}
        <div className="schedule-slide-over-footer">
          <AdvancedSchedulingPanel
            bulkTime={bulkTime}
            bulkInterval={bulkInterval}
            bulkIntervalUnit={bulkIntervalUnit}
            onBulkTimeChange={setBulkTime}
            onBulkIntervalChange={setBulkInterval}
            onBulkIntervalUnitChange={setBulkIntervalUnit}
            onApplyBulk={applyBulkSettings}
          />
          <div className="schedule-slide-over-footer-actions">
            <button
              className="schedule-slide-over-btn schedule-slide-over-btn--danger"
              onClick={handleClearSchedule}
              disabled={!hasAnySchedules}
              title={hasAnySchedules ? "Clear all schedules" : "No schedules to clear"}
            >
              Clear
            </button>
            <button
              className="schedule-slide-over-btn schedule-slide-over-btn--secondary"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              className="schedule-slide-over-btn schedule-slide-over-btn--primary"
              onClick={handleSave}
              disabled={!hasUnsavedChanges}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// Biohack Row Component (collapsed and expanded)
const BiohackRow = ({
  biohack,
  isExpanded,
  scheduleData,
  onToggleExpansion,
  onUpdateSchedule,
  onDone
}) => {
  const [validationErrors, setValidationErrors] = useState({
    interval: false,
    intervalUnit: false,
    time: false
  });

  const displayInterval = scheduleData.interval !== null && scheduleData.interval !== undefined 
    ? scheduleData.interval 
    : null;
  const displayIntervalUnit = scheduleData.intervalUnit || null;

  // Determine if schedule is set - requires all three: number, unit, and time
  const hasNumber = scheduleData.interval !== null && scheduleData.interval !== undefined;
  const hasUnit = scheduleData.intervalUnit !== null && scheduleData.intervalUnit !== undefined;
  const hasTime = scheduleData.times && scheduleData.times.length > 0 && scheduleData.times[0];
  const hasSchedule = hasNumber && hasUnit && hasTime;
  
  let scheduleSummary = 'Not Selected';
  if (hasSchedule) {
    const unitLabel = UNIT_OPTIONS.find(opt => opt.value === scheduleData.intervalUnit)?.label || scheduleData.intervalUnit;
    scheduleSummary = `Every ${scheduleData.interval} ${unitLabel.toLowerCase()} @ ${formatTimeDisplay(scheduleData.times[0])}`;
  }

  const handleDone = (e) => {
    e.stopPropagation();
    
    // Validate all three fields are filled
    const errors = {
      interval: !hasNumber,
      intervalUnit: !hasUnit,
      time: !hasTime
    };

    // Check if there are any errors
    if (errors.interval || errors.intervalUnit || errors.time) {
      setValidationErrors(errors);
      return;
    }

    // Clear any previous errors
    setValidationErrors({ interval: false, intervalUnit: false, time: false });
    onDone();
  };

  const handleIntervalChange = (field, value) => {
    const hasValue = value !== null && value !== undefined && value !== '';
    
    // Clear error when user makes a selection
    if (validationErrors[field] || validationErrors.intervalUnit) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: false,
        ...(field === 'interval' ? { intervalUnit: false } : {})
      }));
    }
    
    if (field === 'interval') {
      const intervalValue = hasValue ? parseInt(value, 10) : null;
      onUpdateSchedule('interval', intervalValue);
      // If clearing interval, also clear unit
      if (!intervalValue) {
        onUpdateSchedule('intervalUnit', null);
      }
    } else if (field === 'intervalUnit') {
      onUpdateSchedule('intervalUnit', hasValue ? value : null);
      // If clearing unit, also clear interval
      if (!hasValue) {
        onUpdateSchedule('interval', null);
      }
    }
  };

  const handleTimeChange = (timeValue) => {
    // Clear error when user makes a selection
    if (validationErrors.time) {
      setValidationErrors(prev => ({ ...prev, time: false }));
    }
    
    if (timeValue) {
      onUpdateSchedule('times', [timeValue]);
    } else {
      onUpdateSchedule('times', []);
    }
  };

  return (
    <div className="biohack-row">
      <div className="biohack-row-collapsed">
        <div className="biohack-row-header">
          <div className="biohack-row-info">
            <img
              src={biohack.image_url || biohack.imageUrl || '/default-biohack.jpg'}
              alt={biohack.name}
              className="biohack-row-image"
              onError={(e) => {
                e.target.src = '/default-biohack.jpg';
              }}
            />
            <span className="biohack-row-name">{biohack.name}</span>
          </div>
          <div className="biohack-row-schedule-section">
            <div className={`biohack-row-summary ${!hasSchedule ? 'biohack-row-summary--empty' : ''}`}>
              {scheduleSummary}
            </div>
          </div>
        </div>
        <div className="biohack-row-expand-control" onClick={onToggleExpansion}>
          <Icon
            name="ChevronDown"
            className={`biohack-row-chevron ${isExpanded ? 'rotated' : ''}`}
          />
        </div>
      </div>

      {isExpanded && (
        <div className="biohack-row-expanded">
          <div className="biohack-row-controls biohack-row-controls--advanced">
            <div className="biohack-row-control-group">
              <label className="biohack-row-label">Number</label>
              <div className="biohack-row-select-wrapper">
                <select
                  value={displayInterval || ''}
                  onChange={(e) => handleIntervalChange('interval', e.target.value)}
                  className={`biohack-row-select ${!displayInterval ? 'biohack-row-select--placeholder' : ''} ${validationErrors.interval ? 'biohack-row-select--error' : ''}`}
                  data-placeholder="1"
                >
                  <option value=""></option>
                  {INTERVAL_NUMBERS.map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
                {!displayInterval && (
                  <span className="biohack-row-select-placeholder">1</span>
                )}
              </div>
            </div>

            <div className="biohack-row-control-group">
              <label className="biohack-row-label">Unit</label>
              <div className="biohack-row-select-wrapper">
                <select
                  value={displayIntervalUnit || ''}
                  onChange={(e) => handleIntervalChange('intervalUnit', e.target.value)}
                  className={`biohack-row-select ${!displayIntervalUnit ? 'biohack-row-select--placeholder' : ''} ${validationErrors.intervalUnit ? 'biohack-row-select--error' : ''}`}
                  data-placeholder="Days"
                >
                  <option value=""></option>
                  {UNIT_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {!displayIntervalUnit && (
                  <span className="biohack-row-select-placeholder">Days</span>
                )}
              </div>
            </div>

            <div className="biohack-row-control-group">
              <label className="biohack-row-label">Time</label>
              <input
                type="time"
                value={scheduleData.times && scheduleData.times.length > 0 ? scheduleData.times[0] : ''}
                onChange={(e) => handleTimeChange(e.target.value)}
                className={`biohack-row-time-input ${validationErrors.time ? 'biohack-row-time-input--error' : ''}`}
              />
            </div>

            <button 
              className="biohack-row-save-btn"
              onClick={handleDone}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Advanced Scheduling Panel Component
const AdvancedSchedulingPanel = ({
  bulkTime,
  bulkInterval,
  bulkIntervalUnit,
  onBulkTimeChange,
  onBulkIntervalChange,
  onBulkIntervalUnitChange,
  onApplyBulk
}) => {
  const [isExpanded, setIsExpanded] = useState(window.innerWidth > 500);
  const isDisabled = !bulkInterval || !bulkIntervalUnit || !bulkTime;

  // Handle resize to ensure it stays expanded on desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 500) {
        setIsExpanded(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleExpand = () => {
    if (window.innerWidth <= 500) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div className={`advanced-scheduling-panel ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div 
        className="advanced-scheduling-header" 
        onClick={toggleExpand}
        role={window.innerWidth <= 500 ? "button" : undefined}
      >
        <h3 className="advanced-scheduling-title">
          <Icon name="Calendar" className="icon--sm" />
          Apply Schedule to All Biohacks
        </h3>
        <div className="advanced-scheduling-toggle-icon">
          <Icon name="ChevronDown" className="icon--sm" />
        </div>
      </div>
      
      <div className="advanced-scheduling-wrapper">
        <div className="advanced-scheduling-content">
          <h4 className="advanced-scheduling-drawer-title">
            Apply Schedule to All Biohacks
          </h4>
          <div className="advanced-scheduling-controls advanced-scheduling-controls--advanced">
            <div className="advanced-scheduling-group">
              <label className="advanced-scheduling-label">Number</label>
              <div className="advanced-scheduling-select-wrapper">
                <select
                  value={bulkInterval}
                  onChange={(e) => onBulkIntervalChange(e.target.value)}
                  className={`advanced-scheduling-select ${!bulkInterval ? 'advanced-scheduling-select--placeholder' : ''}`}
                  data-placeholder="1"
                >
                  <option value=""></option>
                  {INTERVAL_NUMBERS.map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
                {!bulkInterval && (
                  <span className="advanced-scheduling-select-placeholder">1</span>
                )}
              </div>
            </div>

            <div className="advanced-scheduling-group">
              <label className="advanced-scheduling-label">Unit</label>
              <div className="advanced-scheduling-select-wrapper">
                <select
                  value={bulkIntervalUnit}
                  onChange={(e) => onBulkIntervalUnitChange(e.target.value)}
                  className={`advanced-scheduling-select ${!bulkIntervalUnit ? 'advanced-scheduling-select--placeholder' : ''}`}
                  data-placeholder="Days"
                >
                  <option value=""></option>
                  {UNIT_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {!bulkIntervalUnit && (
                  <span className="advanced-scheduling-select-placeholder">Days</span>
                )}
              </div>
            </div>

            <div className="advanced-scheduling-group">
              <label className="advanced-scheduling-label">Time</label>
              <input
                type="time"
                value={bulkTime}
                onChange={(e) => onBulkTimeChange(e.target.value)}
                className="advanced-scheduling-time-input"
              />
            </div>

            <button
              className="advanced-scheduling-apply-btn"
              onClick={onApplyBulk}
              disabled={isDisabled}
            >
              Apply to all unscheduled biohacks
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleSlideOver;
